import React from "react";
import { useDispatch } from "react-redux";
import { Updater, useImmer } from "use-immer";

import { IgTypography } from "@intelligems/ig-theme";
import { ExperienceTestType, OnboardingStateType } from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import { styled } from "@mui/material/styles";

import { usePatchConfigurations } from "../../../api/configurations/patch-configurations";
import { useConfiguration } from "../../../api/configurations/use-configuration";
import { usePostOnboardingForm } from "../../../api/organization-actions/post-onboarding-form";
import { useSubscriptions } from "../../../api/organization-subscriptions/get-subscriptions";
import {
  useGetOrg,
  useGetOrgId,
} from "../../../api/organizations/get-organization";
import { OnboardingReducerAction } from "../../../api/reducers/onboardingReducer";
import { useLoggedInUser } from "../../../api/user-auths/login";
import { usePatchUserWelcome } from "../../../api/users/patch-user-welcome";
import { setDialog } from "../../../store/slices/global";
import { OnboardingSteps } from "../../../types/organization";
import Loading from "../../../utils/Loading";
import { mpCompletedWelcome } from "../../../utils/mixpanel";
import { checkSubscriptionStatus } from "../../ScopeWrappers/helpers";
import { SubscriptionPriceSection } from "../../SubscriptionPriceSection/SubscriptionPriceSection";

import { FindIntelligems } from "./Pages/FindIntelligems";
import { ProductSelection } from "./Pages/ProductFlowSelect";
import { SelectedTestType } from "./Pages/SelectedTestType";
import { WelcomePage } from "./Pages/WelcomePage";

const PREFIX = "Onboarding";

const classes = {
  welcomeHeader: `${PREFIX}-welcomeHeader`,
  continueButton: `${PREFIX}-continueButton`,
  welcomeDescription: `${PREFIX}-welcomeDescription`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.welcomeHeader}`]: {
    color: theme.palette.primary.main,
    fontSize: "23px",
    fontWeight: 600,
    textTransform: "uppercase",
  },
  [`& .${classes.continueButton}`]: {
    width: "268px",
    backgroundColor: theme.palette.primary.main,
  },
  [`& .${classes.welcomeDescription}`]: {
    fontSize: "16px",
    whiteSpace: "pre",
  },
}));

interface OnboardingStepperProps {
  onboardingState: OnboardingStateType;
  dispatchOnboardingState: React.Dispatch<OnboardingReducerAction>;
  selectedTestType: ExperienceTestType | "";
  setSelectedTestType: Updater<ExperienceTestType | "">;
  steps: OnboardingSteps[];
  setResetOnboardingFlow: Updater<boolean>;
}

export const OnboardingStepper = ({
  onboardingState,
  dispatchOnboardingState,
  setSelectedTestType,
  selectedTestType,
  steps,
  setResetOnboardingFlow,
}: OnboardingStepperProps) => {
  const dispatch = useDispatch();
  const mutateUserWelcome = usePatchUserWelcome();
  const user = useLoggedInUser();
  const mutateConfigurations = usePatchConfigurations(true);
  const postFeedbackAnswers = usePostOnboardingForm();
  const organization = useGetOrg();
  const orgId = useGetOrgId();

  const subscriptions = useSubscriptions({ orgId: organization.data.org.id });

  const isSubscriptionActive = checkSubscriptionStatus(true);
  const selectedTestTypeConfig = useConfiguration<string>(
    "onboarding_test_flow"
  );

  const shopifyPlan = useConfiguration("shopify_plan");
  const [source, setSource] = useImmer("");
  const [sourceSpecify, setSourceSpecify] = useImmer("");

  const finishOnboarding = () => {
    if (
      user.isSuccess &&
      user.data?.user.showWelcome &&
      organization.isSuccess
    ) {
      mpCompletedWelcome(user.data.user.email);
      user.data?.user.id &&
        mutateUserWelcome.mutate({
          showWelcome: false,
          userId: user.data.user.id,
          organizationId: organization.data.org.id,
          email: user.data.user.email,
        });
    }
    mutateConfigurations.mutate([
      {
        topic: "UI_CONFIG",
        key: "onboarding_test_flow",
        value: selectedTestType || "",
        type: "String",
      },
    ]);

    if (source.length > 0) {
      postFeedbackAnswers.mutate({
        shopifyPlan: shopifyPlan?.value ?? "",
        shopId: organization.data.org.shopId,
        response: {
          source,
          sourceSpecify,
        },
      });
    }
    setResetOnboardingFlow(true);
    dispatch(setDialog({ dialog: "onboarding", open: false }));
  };

  const handleNext = () => {
    if (
      onboardingState.activeStep === 2 &&
      selectedTestTypeConfig!.value !== "" &&
      isSubscriptionActive
    ) {
      finishOnboarding();
    } else if (
      onboardingState.activeStep === 4 ||
      (onboardingState.activeStep === 3 && isSubscriptionActive)
    ) {
      finishOnboarding();
    } else if (
      onboardingState.activeStep === 2 &&
      selectedTestTypeConfig!.value !== "" &&
      !isSubscriptionActive
    ) {
      dispatchOnboardingState({
        type: "gotoStep",
        value: 4,
      });
    } else {
      dispatchOnboardingState({
        type: "buttonClick",
        payload: {
          direction: "next",
        },
      });
    }
  };

  const renderOnboardingPage = (activeStep: number) => {
    switch (activeStep) {
      case 0:
        return <WelcomePage handleNext={handleNext} />;
      case 1:
        return (
          <ProductSelection
            setSelectedTestType={setSelectedTestType}
            handleNext={handleNext}
            selectedTestType={selectedTestType}
          />
        );
      case 2:
        return (
          <SelectedTestType
            selectedTestType={selectedTestType}
            handleNext={handleNext}
          />
        );
      case 3:
        return (
          <FindIntelligems
            selectedType={source}
            setSelectedType={setSource}
            setSpecifyValue={setSourceSpecify}
            specifyValue={sourceSpecify}
            handleNext={handleNext}
          />
        );

      case 4:
        return (
          <SubscriptionPriceSection
            onSetupLaterClick={handleNext}
            selectedTestType={selectedTestType}
          />
        );
      default:
        return <WelcomePage handleNext={handleNext} />;
    }
  };

  if (subscriptions.isLoading || user.isLoading) {
    return (
      <div style={{ overflowX: "hidden", position: "relative" }}>
        <Loading />
      </div>
    );
  }

  if (subscriptions.isSuccess && user.isSuccess) {
    return (
      <Root>
        <Grid
          container
          direction={"column"}
          rowSpacing={1}
          alignItems={"center"}
          textAlign={"center"}
          sx={{
            padding: "14px",
            paddingTop: 0,
          }}
        >
          <Grid item zIndex={1000} position={"relative"}>
            <IgTypography className={classes.welcomeHeader}>
              {steps[onboardingState.activeStep].label}
            </IgTypography>
          </Grid>
          <Grid item width={"600px"} zIndex={1000} position={"relative"}>
            <IgTypography className={classes.welcomeDescription}>
              {steps[onboardingState.activeStep].description}
            </IgTypography>
          </Grid>
        </Grid>
        <Grid
          container
          direction={"column"}
          justifyContent={"center"}
          rowSpacing={2}
          sx={{
            padding: "14px",
            overflow: "auto",
            maxHeight: "1000px",
          }}
        >
          <Grid item>{renderOnboardingPage(onboardingState.activeStep)}</Grid>
        </Grid>
      </Root>
    );
  }
  return null;
};
